import { FormCounter } from '@texas/components/shared/form/FormCounter';
import { ArticleNumberAllowance } from '../numbering/ArticleNumberAllowance';
import { ArticleRangeClash } from '../numbering/ArticleRangeClash';
import { articleDescriptionMaxLength } from '@texas/resources/constants';
import {
  Badge,
  Box,
  Divider,
  Flex,
  FormControl,
  FormErrorMessage,
  Grid,
  Input,
  Radio,
  RadioGroup,
  Stack,
  Text,
} from '@chakra-ui/react';
import { ValidateItemCodeResponse } from '@texas/api/endpoints/articlesApi';
import { WarningComponent } from '@texas/components/shared/WarningComponent';
import { TexasFormLabel } from '@texas/components/shared/form/TexasFormLabel';
import { TexasSelect } from '@texas/components/shared/form/TexasSelect';
import { NewConcept } from '@texas/components/customer/newConcept/NewConcept';
import { NewCustomer } from '@texas/components/customer/newCustomer/NewCustomer';
import {
  articleWorkspaceStyles,
  copyArticleSelectStyles,
  copyArticleStyles,
} from '@texas/resources/theme/styles/entityWorkspace';
import { ReactSelectOption } from '@texas/types';
import { useTranslation } from 'react-i18next';
import {
  ArticleNumberInput,
  ArticleNumberPosition,
} from './ArticleNumberFields';
import { CopyArticleSectionHeading } from './CopyArticleSectionHeading';
import { CopyMode, useCopyArticle } from './useCopyArticle';

export function CopyArticleDestination() {
  const { t } = useTranslation();
  const {
    setValue,
    fieldWarnings,
    targets,
    firstArticleClashes,
    suggestedRange,
    applySuggestedRange,
    availability,
    checking,
    conceptOptions,
    copyMode,
    copyTargets,
    destinationCustomerId,
    destinationCustomerOption,
    destinationCustomerSearch,
    hasValidItemCodeLength,
    itemCode,
    itemCodeMaxLength,
    itemCodeMinLength,
    name,
    padding,
    position,
    rangeEnd,
    rangeFormatMaxLength,
    rangeNumberLength,
    rangeStart,
    selectDestinationConcept,
    selectDestinationCustomer,
    selectedConceptOption,
  } = useCopyArticle();

  return (
    <Box sx={articleWorkspaceStyles.card}>
      <CopyArticleSectionHeading
        number="3"
        title={t('article.copy.destinationTitle')}
        description={t('article.copy.destinationDescription')}
      />
      <Stack spacing={5} sx={copyArticleStyles.cardBody}>
        <Box>
          <Text sx={copyArticleStyles.destinationTitle}>
            {t('article.copy.destinationTitle')}
          </Text>
          <Grid sx={copyArticleStyles.destinationGrid}>
            <FormControl
              sx={articleWorkspaceStyles.numberField}
              id={fieldWarnings.destinationCustomer.targetId}
              isInvalid={!!fieldWarnings.destinationCustomer.warning}
              isRequired={true}
            >
              <WarningComponent {...fieldWarnings.destinationCustomer}>
                <TexasFormLabel>{t('article.copy.customer')}</TexasFormLabel>
              </WarningComponent>
              <TexasSelect<ReactSelectOption>
                {...copyArticleSelectStyles}
                inputValue={destinationCustomerSearch.input}
                isLoading={destinationCustomerSearch.loading}
                noOptionsMessage={() =>
                  destinationCustomerSearch.input.trim().length < 2
                    ? t('article.copy.customerSearchMinimum')
                    : t('general.noResult')
                }
                onChange={(option) => {
                  if (!option) return;
                  selectDestinationCustomer(option);
                }}
                onInputChange={destinationCustomerSearch.setInput}
                options={destinationCustomerSearch.options}
                placeholder={t('article.copy.chooseSourceCustomer')}
                value={destinationCustomerOption}
              />
              <NewCustomer
                buttonLabel={t('article.copy.newCustomerAction')}
                buttonProps={{ variant: 'formInlineCreate' }}
              />
            </FormControl>
            <FormControl sx={articleWorkspaceStyles.numberField}>
              <TexasFormLabel>{t('article.copy.concept')}</TexasFormLabel>
              <TexasSelect<ReactSelectOption>
                {...copyArticleSelectStyles}
                isDisabled={!destinationCustomerId}
                isClearable={true}
                noOptionsMessage={() => t('general.noResult')}
                onChange={(option) => {
                  selectDestinationConcept(
                    option ? Number(option.value) : null,
                  );
                }}
                options={conceptOptions}
                placeholder={t('article.copy.noConcept')}
                value={selectedConceptOption}
              />
              <NewConcept
                customerId={destinationCustomerId || undefined}
                buttonLabel={t('article.copy.newConceptAction')}
                buttonProps={{
                  isDisabled: !destinationCustomerId,
                  variant: 'formInlineCreate',
                }}
              />
            </FormControl>
          </Grid>
        </Box>

        <Divider />

        <FormControl sx={copyArticleStyles.modeControl}>
          <TexasFormLabel>{t('article.copy.createMode')}</TexasFormLabel>
          <RadioGroup
            value={copyMode}
            onChange={(value) => setValue('copyMode', value as CopyMode)}
          >
            <Stack direction="row" spacing={6}>
              <Radio value="single">{t('article.copy.single')}</Radio>
              <Radio value="range">{t('article.copy.range')}</Radio>
            </Stack>
          </RadioGroup>
        </FormControl>

        <Divider />

        <Grid sx={copyArticleStyles.fieldsGrid}>
          <FormControl
            sx={copyArticleStyles.fieldControl}
            isInvalid={!!fieldWarnings.itemCode.warning}
            id={fieldWarnings.itemCode.targetId}
            isRequired={true}
          >
            <WarningComponent {...fieldWarnings.itemCode}>
              <TexasFormLabel>
                {copyMode === 'single'
                  ? t('article.copy.articleNumber')
                  : t('article.copy.numberFormat')}
              </TexasFormLabel>
            </WarningComponent>
            <Input
              variant="texas"
              value={itemCode}
              maxLength={
                copyMode === 'range' ? rangeFormatMaxLength : itemCodeMaxLength
              }
              onChange={(e) => setValue('itemCode', e.target.value)}
            />
            <FormCounter
              length={itemCode.length}
              maxLength={rangeFormatMaxLength}
            />
            <ArticleNumberAllowance
              maximumLength={itemCodeMaxLength}
              reservedDigits={rangeNumberLength}
              inputMaxLength={rangeFormatMaxLength}
            />
            <FormErrorMessage>
              {t('article.copy.itemCodeLengthRequirement', {
                minimum: itemCodeMinLength,
                maximum: itemCodeMaxLength,
              })}
            </FormErrorMessage>
          </FormControl>
          <FormControl
            sx={copyArticleStyles.fieldControl}
            isInvalid={!!fieldWarnings.description.warning}
            id={fieldWarnings.description.targetId}
            isRequired={true}
          >
            <WarningComponent {...fieldWarnings.description}>
              <TexasFormLabel>{t('article.copy.articleName')}</TexasFormLabel>
            </WarningComponent>
            <Input
              variant="texas"
              value={name}
              maxLength={articleDescriptionMaxLength}
              onChange={(e) => setValue('name', e.target.value)}
            />
            <FormErrorMessage>
              {name.trim()
                ? t('errors.maxLength', { count: articleDescriptionMaxLength })
                : t('errors.nameRequired', { name: t('general.description') })}
            </FormErrorMessage>
          </FormControl>
        </Grid>
        {copyMode === 'range' && (
          <Grid sx={copyArticleStyles.rangeGrid}>
            <ArticleNumberPosition
              label={t('article.copy.numberPosition')}
              value={position}
              onChange={(value) => setValue('position', value)}
              variant="texas"
            />
            <ArticleNumberInput
              variant="texas"
              max={9999}
              label={t('article.copy.from')}
              warning={fieldWarnings.range}
              value={rangeStart}
              onChange={(value) => setValue('rangeStart', value)}
            />
            <ArticleNumberInput
              variant="texas"
              max={9999}
              label={t('article.copy.to')}
              value={rangeEnd}
              onChange={(value) => setValue('rangeEnd', value)}
            />
            <ArticleNumberInput
              variant="texas"
              label={t('article.copy.padding')}
              value={padding}
              onChange={(value) => setValue('padding', value)}
              min={1}
              max={Math.min(6, itemCodeMaxLength)}
            />
          </Grid>
        )}

        {firstArticleClashes ? (
          <ArticleRangeClash
            mode={copyMode}
            itemCode={targets[0].itemCode}
            suggestedRange={suggestedRange}
            onApply={applySuggestedRange}
          />
        ) : (
          <Box sx={copyArticleStyles.preview}>
            <Flex sx={copyArticleStyles.previewHeader}>
              <Text sx={copyArticleStyles.previewTitle}>
                {t('article.copy.generatedPreview')}
              </Text>
              <Text sx={copyArticleStyles.smallMuted}>
                {copyTargets.length} {t('article.copy.articles')}
              </Text>
            </Flex>
            <Stack
              divider={<Divider />}
              sx={copyArticleStyles.previewList}
              spacing={0}
            >
              {copyTargets.length === 0 && (
                <Text sx={copyArticleStyles.previewEmpty}>
                  {t('article.copy.previewEmpty')}
                </Text>
              )}
              {targets.map((target) => (
                <Flex key={target.itemCode} sx={copyArticleStyles.previewRow}>
                  <Box>
                    <Text sx={copyArticleStyles.previewCode}>
                      {target.itemCode}
                    </Text>
                    <Text sx={copyArticleStyles.previewName}>
                      {target.name}
                    </Text>
                    {!copyTargets.some(
                      (copyTarget) => copyTarget.itemCode === target.itemCode,
                    ) && (
                      <Text sx={copyArticleStyles.smallMuted}>
                        {t('article.create.skippedUnavailableArticle', {
                          itemCode: target.itemCode,
                        })}
                      </Text>
                    )}
                  </Box>
                  <AvailabilityBadge
                    result={availability[target.itemCode]}
                    checking={checking}
                    validLength={hasValidItemCodeLength(target.itemCode)}
                  />
                </Flex>
              ))}
            </Stack>
          </Box>
        )}
      </Stack>
    </Box>
  );
}

function AvailabilityBadge({
  result,
  checking,
  validLength,
}: {
  result?: ValidateItemCodeResponse;
  checking: boolean;
  validLength: boolean;
}) {
  const { t } = useTranslation();
  const isError = !validLength || result?.isValid === false;
  const errorLabel =
    result?.errorType === 'InUseByErp'
      ? t('article.copy.existsInGarp')
      : result?.errorType === 'ArchivedInTexas'
      ? t('article.copy.archivedInTexas')
      : result?.errorType === 'InUseByTexas'
      ? t('article.copy.existsInTexas')
      : t('article.copy.validationFailed');
  return (
    <Badge
      sx={copyArticleStyles.availabilityBadge}
      colorScheme={isError ? 'red' : 'green'}
    >
      {!validLength
        ? t('article.copy.invalidLength')
        : checking || result === undefined
        ? t('article.copy.checking')
        : result.isValid
        ? t('article.copy.available')
        : errorLabel}
    </Badge>
  );
}
